import { useEffect, useRef } from "react";
import { useParams } from "react-router";
import { useCurrentUrl } from "@shared-ui/hooks/useCurrentUrl";
import { validSessionToken } from "@shared-ui/utils/token";
import {
  clearRememberMeFromStorage,
  getRememberMeFromStorage,
  getLastLoginId,
  setAliasReturnTenant,
  getAliasReturnTenant,
} from "@/utils/storage";
import { usePortalAuthClient } from "./hooks/usePortalAuthClient";
import { useLoginState } from "./hooks/useLoginState";
import { useLoginHandlers, handleAuthError } from "./hooks/useLoginHandlers";
import { LoginForm } from "./components/LoginForm";
import { NewPasswordUI } from "./components/NewPasswordUI";
import { MFACodeUI } from "./components/MFACodeUI";
import { EmailCodeUI } from "./components/EmailCodeUI";
import { ForgotPasswordUI } from "./components/ForgotPasswordUI";
import { ResetPasswordUI } from "./components/ResetPasswordUI";
import { TOTPSetupUI } from "./components/TOTPSetupUI";
import { TenantSelect } from "./components/TenantSelect";
import { ProductSelect } from "./components/ProductSelect";
import { PasskeyPromptModal } from "./components/PasskeyPromptModal";
import type { LoginScreen } from "./types";

/**
 * 現在の状態から表示すべき画面を判定する（優先度順）
 */
function getLoginScreen(
  state: ReturnType<typeof useLoginState>,
  hasUser: boolean,
): LoginScreen {
  if (state.isSessionChecking) return "sessionChecking";
  if (state.challengeType === "RESET_PASSWORD") return "challengeResetPassword";
  if (state.resetFlowStep === "email") return "forgotPasswordEmail";
  if (state.resetFlowStep === "code") return "forgotPasswordCode";
  if (state.challengeType === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED")
    return "challengeNewPassword";
  if (
    state.challengeType === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" ||
    state.challengeType === "CONFIRM_SIGN_IN_WITH_SMS_CODE"
  )
    return "challengeMfaCode";
  if (state.challengeType === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE")
    return "challengeEmailCode";
  if (state.showTOTPSetup && state.totpUri && state.totpSecretKey)
    return "totpSetup";
  if (state.showPasskeyPromptModal && hasUser) return "passkeyPromptModal";
  if (state.showTenantSelect && hasUser) return "tenantSelect";
  if (state.showSelectUI && hasUser) return "productSelect";
  return "login";
}

/**
 * ログインページ
 */
export const LoginPage: React.FC = () => {
  // State管理
  const state = useLoginState();

  // クライアントとクエリパラメータ
  const client = usePortalAuthClient();
  const { query } = useCurrentUrl();
  const needsLogoutFromQuery = query.get("logout");
  // PRODUCT_DEV-549: プロダクト側の自動ログイン再試行が失敗した戻り。手動ログインを表示するが
  // signOut(revoke) はしない（共有 refreshToken を温存）。`?logout`(明示=global revoke) と区別する。
  const reauthFromQuery = query.get("reauth");
  const tenantIdFromQuery = query.get("tenantId");
  const productIdFromQuery = query.get("productId");
  const actionFromQuery = query.get("action");
  const emailFromQuery = query.get("email");
  const codeFromQuery = query.get("code");
  const mfaFromQuery = query.get("mfa");
  // PRODUCT_DEV-396 テスト用: ?forcePasskeyPrompt=1 で常にモーダル表示、=0 で非表示
  const forcePasskeyPromptFromQuery = query.get("forcePasskeyPrompt");

  // PRODUCT_DEV-421 alias ログインモード: URL path `/:tenantId` または `?tenant=<tenantId>` から取得
  const params = useParams<{ tenantId?: string }>();
  const aliasTenantId =
    params.tenantId?.trim() || query.get("tenant")?.trim() || null;

  // PRODUCT_DEV-484 エイリアス由来セッションの戻り先を sessionStorage に保持。
  // ポータル内のどの画面でログアウトしてもエイリアスログイン画面へ戻すために使う。
  useEffect(() => {
    if (aliasTenantId) setAliasReturnTenant(aliasTenantId);
  }, [aliasTenantId]);

  // PRODUCT_DEV-484 ログアウト後の遷移先。
  // エイリアス由来セッションならエイリアスログイン画面 (`/<tenantId>`)、それ以外はポータルルート。
  const redirectAfterLogout = () => {
    const base = `${location.origin}${import.meta.env.BASE_URL}`;
    const aliasReturn = getAliasReturnTenant();
    window.location.href = aliasReturn ? `${base}${aliasReturn}` : base;
  };

  // ハンドラー関数
  const handlers = useLoginHandlers(
    client,
    state,
    tenantIdFromQuery,
    productIdFromQuery,
    mfaFromQuery,
    forcePasskeyPromptFromQuery,
    aliasTenantId,
  );

  // 初期処理: セッションの検証と自動ログイン
  useEffect(() => {
    // パスワードリセット完了後の遷移処理
    // /?reset=ok&email=... で到達した場合、ログイン画面に成功メッセージを出して email を prefill する
    const resetDone = query.get("reset");
    if (resetDone === "ok") {
      const emailParam = query.get("email");
      state.setRecoverySuggestion(
        "パスワードがリセットされました。新しいパスワードでログインしてください。",
      );
      if (emailParam) state.setEmail(decodeURIComponent(emailParam));
      // クエリパラメータを履歴から掃除
      window.history.replaceState({}, "", window.location.pathname);
    }

    const initializeSession = async () => {
      try {
        // URLパラメータからのパスワードリセット
        if (actionFromQuery === "reset" && emailFromQuery) {
          state.setResetFlowStep("code");
          state.setResetEmail(emailFromQuery);
          if (codeFromQuery) {
            state.setResetCode(codeFromQuery);
          }
          state.setIsSessionCheking(false);
          return;
        }

        // 強制ログアウト
        if (needsLogoutFromQuery !== null) {
          await client.signOut({ reason: "query-logout" });
          clearRememberMeFromStorage();
          // PRODUCT_DEV-484 エイリアス由来セッションの強制ログアウト（例: プロダクト側からの
          // `?logout` 戻り）はエイリアスログイン画面へ戻す。既にエイリアスURL上なら遷移不要。
          const aliasReturn = getAliasReturnTenant();
          if (aliasReturn && !aliasTenantId) {
            window.location.href = `${location.origin}${import.meta.env.BASE_URL}${aliasReturn}`;
            return;
          }
        }

        // PRODUCT_DEV-549: プロダクト側の自動ログイン再試行が失敗した戻り(?reauth)。
        // 無限リダイレクト防止のため auto-redirect せず手動ログインを表示する。
        // signOut(revoke) はしない（共有 refreshToken を温存し他 product/他デバイスを守る）。
        if (reauthFromQuery !== null) {
          state.setIsSessionCheking(false);
          return;
        }

        // 「次回から自動的にログイン」でない場合は auto-login しない（下の savedRememberMe ゲートで制御）。
        // PRODUCT_DEV-549: ここで signOut すると共有 refreshToken が revoke され全 product が落ちるため呼ばない。
        const savedRememberMe = getRememberMeFromStorage();

        // セッションの取得
        const tokens = await validSessionToken();

        // 「次回から自動的にログイン」かつ有効期限内のトークンが存在する場合
        if (savedRememberMe && tokens) {
          // トークンを設定してプロダクト選択へ
          client.setTokens(tokens.idToken, tokens.accessToken);
          // ユーザ情報の取得～プロダクト選択画面またはリダイレクト（トークン残存時はパスキー設定スキップ）
          await handlers.whoamiAndCheckProduct({ skipPasskeySetup: true });
        }

        state.setIsSessionCheking(false);
      } catch (err) {
        handleAuthError(err, state, "ログインに失敗しました");
        // PRODUCT_DEV-549: 一時エラーで signOut(revoke) すると共有 refreshToken が失効し
        // 全 product が落ちるため呼ばない。ログイン画面表示のみ。
        clearRememberMeFromStorage();
        state.setIsSessionCheking(false);
      }
    };
    initializeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PRODUCT_DEV-396 セッション確認後、ログインIDが保存済 or ブラウザ autofill されたら
  // 自動的に login-options を叩き、パスキー利用可なら handleEmailBlur 経由でパスキー認証を開始する
  // 200ms × 最大15回 (合計3秒) ポーリング: 1回 200ms だと autofill のタイミング揺れで取りこぼすため。
  // ID が取れた時点で1回だけ handleEmailBlur を呼んで終了。15回経っても空なら静かに諦める
  const passkeyAutoTriggered = useRef(false);
  useEffect(() => {
    if (state.isSessionChecking || passkeyAutoTriggered.current) return;
    passkeyAutoTriggered.current = true;

    const savedLoginId = getLastLoginId();
    if (savedLoginId) {
      state.setEmail(savedLoginId);
    }

    let attempts = 0;
    const MAX_ATTEMPTS = 15;
    const INTERVAL_MS = 200;

    const intervalId = setInterval(() => {
      attempts++;
      const inputEl = document.getElementById(
        "login-email",
      ) as HTMLInputElement | null;
      const domValue = inputEl?.value?.trim() ?? "";
      const id = (savedLoginId ?? "").trim() || domValue;

      if (id) {
        clearInterval(intervalId);
        if (!savedLoginId && domValue) state.setEmail(domValue);
        handlers.handleEmailBlur(id);
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(intervalId);
      }
    }, INTERVAL_MS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isSessionChecking]);

  // 画面判定
  const screen = getLoginScreen(state, !!client.me);

  switch (screen) {
    case "sessionChecking":
      return <div>Session checking...</div>;

    case "challengeResetPassword":
      return (
        <ResetPasswordUI
          loading={state.loading}
          error={state.error}
          recoverySuggestion={state.recoverySuggestion}
          email={state.resetEmail}
          code={state.resetCode}
          newPassword={state.resetPassword}
          confirmPassword={state.resetConfirmPassword}
          onEmailChange={state.setResetEmail}
          onCodeChange={state.setResetCode}
          onNewPasswordChange={state.setResetPassword}
          onConfirmPasswordChange={state.setResetConfirmPassword}
          onSubmit={handlers.handleResetPasswordSubmit}
          onBack={handlers.handleChallengeCancel}
          emailLocked={!!emailFromQuery}
          codeLocked={!!codeFromQuery}
        />
      );

    case "forgotPasswordEmail":
      return (
        <ForgotPasswordUI
          loading={state.loading}
          error={state.error}
          email={state.resetEmail}
          onEmailChange={state.setResetEmail}
          onSubmit={handlers.handleSendResetCode}
          onCancel={handlers.handleResetCancel}
        />
      );

    case "forgotPasswordCode":
      return (
        <ResetPasswordUI
          loading={state.loading}
          error={state.error}
          recoverySuggestion={state.recoverySuggestion}
          email={state.resetEmail}
          code={state.resetCode}
          newPassword={state.resetPassword}
          confirmPassword={state.resetConfirmPassword}
          onEmailChange={state.setResetEmail}
          onCodeChange={state.setResetCode}
          onNewPasswordChange={state.setResetPassword}
          onConfirmPasswordChange={state.setResetConfirmPassword}
          onSubmit={handlers.handleResetPasswordSubmit}
          onBack={handlers.handleResetBack}
          emailLocked={!!emailFromQuery}
          codeLocked={!!codeFromQuery}
        />
      );

    case "challengeNewPassword":
      return (
        <NewPasswordUI
          loading={state.loading}
          error={state.error}
          recoverySuggestion={state.recoverySuggestion}
          newPassword={state.newPassword}
          confirmPassword={state.confirmPassword}
          onNewPasswordChange={state.setNewPassword}
          onConfirmPasswordChange={state.setConfirmPassword}
          onSubmit={handlers.handleNewPasswordSubmit}
          onCancel={handlers.handleChallengeCancel}
        />
      );

    case "challengeMfaCode":
      return (
        <MFACodeUI
          challengeType={state.challengeType!}
          loading={state.loading}
          error={state.error}
          recoverySuggestion={state.recoverySuggestion}
          mfaCode={state.mfaCode}
          onMfaCodeChange={state.setMfaCode}
          onSubmit={handlers.handleMfaCodeSubmit}
          onCancel={handlers.handleChallengeCancel}
        />
      );

    case "challengeEmailCode":
      return (
        <EmailCodeUI
          loading={state.loading}
          error={state.error}
          recoverySuggestion={state.recoverySuggestion}
          code={state.emailCode}
          onCodeChange={state.setEmailCode}
          onSubmit={handlers.handleEmailCodeSubmit}
          onCancel={handlers.handleChallengeCancel}
        />
      );

    case "totpSetup":
      return (
        <TOTPSetupUI
          totpUri={state.totpUri!}
          secretKey={state.totpSecretKey!}
          verifyCode={state.totpVerifyCode}
          loading={state.loading}
          error={state.error}
          recoverySuggestion={state.recoverySuggestion}
          onVerifyCodeChange={state.setTotpVerifyCode}
          onSubmit={handlers.handleTOTPSetupSubmit}
          onCancel={handlers.handleTOTPSetupCancel}
        />
      );

    case "passkeyPromptModal":
      // PRODUCT_DEV-396 ログイン後パスキー登録促進モーダル (単独画面で表示)
      return (
        <PasskeyPromptModal
          loading={state.loading}
          onSetup={handlers.handlePasskeyPromptSetup}
          onLater={handlers.handlePasskeyPromptLater}
        />
      );

    case "tenantSelect":
      return (
        <TenantSelect
          user={client.me!}
          tenants={state.tenants}
          onSelect={async (t) => {
            try {
              state.setError(null);
              state.setRecoverySuggestion(null);
              await handlers.handleTenantChoice(t);
            } catch (err) {
              handleAuthError(err, state, "テナント選択に失敗しました");
            }
          }}
          handleClickLogout={async () => {
            await client.signOut({ reason: "tenant-logout-btn" });
            redirectAfterLogout();
          }}
        />
      );

    case "productSelect":
      return (
        <ProductSelect
          user={client.me!}
          products={state.products}
          onSelect={async (p) => {
            try {
              // 選択したプロダクトをローディング表示
              const newProducts = state.products.map((one) => {
                if (one.id === p.id && one.tenantId === p.tenantId) {
                  return { ...one, isLoading: true };
                }
                return one;
              });
              state.setProducts(newProducts);
              state.setError(null);
              state.setRecoverySuggestion(null);
              state.setLoading(true);
              // リダイレクト
              await handlers.handleProductChoice({
                url: p.url,
                productId: p.id,
                tenantId: p.tenantId,
              });
            } catch (err) {
              handleAuthError(err, state, "プロダクト遷移に失敗しました");
              // PRODUCT_DEV-549: 遷移失敗で signOut(revoke) すると共有 refreshToken が失効し
              // 全 product が落ちるため呼ばない。エラー表示のみ。
            } finally {
              state.setLoading(false);
              state.setLoadingMessage("");
            }
          }}
          handleClickLogout={async () => {
            await client.signOut({ reason: "product-logout-btn" });
            redirectAfterLogout();
          }}
        />
      );

    case "login":
      return (
        <LoginForm
          email={state.email}
          password={state.password}
          rememberMe={state.rememberMe}
          loading={state.loading}
          loadingMessage={state.loadingMessage}
          error={state.error}
          recoverySuggestion={state.recoverySuggestion}
          onEmailChange={state.setEmail}
          onEmailBlur={handlers.handleEmailBlur}
          onPasswordChange={state.setPassword}
          onRememberMeChange={state.setRememberMe}
          onSubmit={handlers.handleLogin}
          onForgotPassword={handlers.handleForgotPassword}
          aliasTenantId={aliasTenantId}
        />
      );
  }
};
